import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Loader2, TrendingUp, X, EyeOff, Info, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import CampaignCard from '../campaigns/CampaignCard';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or get session ID for tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('fth_session_id');
  if (!sessionId) {
    sessionId = 'ses_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('fth_session_id', sessionId);
  }
  return sessionId;
};

// Check if user has consented to analytics
const hasAnalyticsConsent = () => {
  try {
    const consent = JSON.parse(localStorage.getItem('cookie_consent') || '{}');
    return consent.analytics === true;
  } catch {
    return false;
  }
};

const RecommendedSection = ({ 
  variant = 'home', // 'home' or 'discover'
  category = null,
  limit = 6,
  showTitle = true 
}) => {
  const { user, token } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [personalized, setPersonalized] = useState(false);
  const [hiddenCampaigns, setHiddenCampaigns] = useState(new Set());
  const [error, setError] = useState(null);
  
  const sessionId = getSessionId();

  // Fetch recommendations
  const fetchRecommendations = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = variant === 'home' 
        ? `/api/recommendations/home` 
        : `/api/recommendations/discover`;
      
      const params = new URLSearchParams({
        session_id: sessionId,
        limit: limit.toString()
      });
      
      if (category) {
        params.append('category', category);
      }

      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}${endpoint}?${params}`, { headers });
      
      if (!response.ok) {
        throw new Error('Failed to fetch recommendations');
      }
      
      const data = await response.json();
      
      // Filter out hidden campaigns
      const visible = (data.recommendations || []).filter(
        r => !hiddenCampaigns.has(r.id)
      );
      
      setRecommendations(visible);
      setPersonalized(data.personalized || false);
      
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  }, [variant, category, limit, token, sessionId, hiddenCampaigns]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  // Track view event when campaign is clicked
  const trackViewEvent = async (campaignId, campaignCategory) => {
    if (!hasAnalyticsConsent()) return;
    
    try {
      await fetch(`${API_URL}/api/events/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          session_id: sessionId,
          event_type: 'view_campaign',
          campaign_id: campaignId,
          category: campaignCategory,
          analytics_consent: true
        })
      });
    } catch (err) {
      console.error('Failed to track event:', err);
    }
  };

  // Hide a recommendation
  const hideRecommendation = async (campaignId, reason = null) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await fetch(`${API_URL}/api/recommendations/hide`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          campaign_id: campaignId,
          session_id: sessionId,
          reason
        })
      });
      
      // Update local state
      setHiddenCampaigns(prev => new Set([...prev, campaignId]));
      setRecommendations(prev => prev.filter(r => r.id !== campaignId));
      
    } catch (err) {
      console.error('Failed to hide recommendation:', err);
    }
  };

  // Get display title based on state
  const getTitle = () => {
    if (personalized) {
      return 'Recommended for You';
    }
    return variant === 'home' ? 'Trending Now' : 'Discover Campaigns';
  };

  if (loading) {
    return (
      <section className="py-8 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        </div>
      </section>
    );
  }

  if (error || recommendations.length === 0) {
    return null; // Don't show section if no recommendations
  }

  return (
    <section className="py-8 bg-background" data-testid="recommended-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showTitle && (
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
            <div className="flex items-center gap-3">
              {personalized ? (
                <Sparkles className="w-5 h-5 text-amber-500" />
              ) : (
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              )}
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                {getTitle()}
              </h2>
              {personalized && (
                <Badge variant="secondary" className="bg-amber-500/20 text-amber-400">
                  Personalized
                </Badge>
              )}
            </div>
            {variant === 'home' && (
              <Link to="/discover?all=true" className="mt-4 sm:mt-0">
                <Button variant="ghost" size="sm" className="text-emerald-500 hover:text-emerald-400">
                  View All <ArrowRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recommendations.map((campaign) => (
            <RecommendedCampaignCard
              key={campaign.id}
              campaign={campaign}
              onView={() => trackViewEvent(campaign.id, campaign.category)}
              onHide={() => hideRecommendation(campaign.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Campaign card wrapper with recommendation features
const RecommendedCampaignCard = ({ campaign, onView, onHide }) => {
  const [showHideOption, setShowHideOption] = useState(false);

  // Convert recommendation format to CampaignCard format
  const formattedCampaign = {
    id: campaign.id,
    title: campaign.title,
    story: campaign.story,
    goal: campaign.goal,
    raised: campaign.raised,
    donors: campaign.donors,
    category: campaign.category,
    subcategory: campaign.subcategory,
    image: campaign.image,
    creator: campaign.creator,
    location: campaign.location,
    daysLeft: campaign.days_left,
    trending: campaign.trending,
  };

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowHideOption(true)}
      onMouseLeave={() => setShowHideOption(false)}
      data-testid={`recommended-campaign-${campaign.id}`}
    >
      <Link to={`/campaign/${campaign.id}`} onClick={onView}>
        <CampaignCard campaign={formattedCampaign} variant="featured" />
      </Link>
      
      {/* Hide option */}
      {showHideOption && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-3 right-3 h-8 w-8 p-0 bg-black/60 text-white hover:bg-black/80 rounded-full z-10"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onHide();
          }}
          data-testid="hide-recommendation-btn"
        >
          <EyeOff className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

export default RecommendedSection;
